import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { getFindDegreesTemplate } from '../data/findDegreesTemplates';
import { getProgramTemplate, programsIndexSlug } from '../data/programTemplates';
import { ProgramTemplatePage } from './ProgramTemplatePage';
import { ProgramDomainsPage } from './ProgramDomainsPage';
import { DegreeLandingTemplate } from '../components/DegreeLandingTemplate';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * Resolves a slug to the right template: the program index, a domain page, an
 * individual program page, or the original lead-gen landing template. Explicit
 * routes are declared in App.tsx (so each URL shows up on its own), which means
 * the slug is read from the path when there is no `:template` param to read.
 *
 * Program content lives under `/program-information/`; anything reaching the
 * legacy `/find-degrees/` path with a program slug is redirected there so old
 * links keep working.
 */
export function FindDegreesPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const pathSlug = location.pathname.split('/').filter(Boolean).pop() || '';
  const slug = (template || pathSlug).toLowerCase();
  const isLegacyPath = location.pathname.startsWith('/find-degrees/');
  const program = getProgramTemplate(slug);
  const isProgramContent = slug === programsIndexSlug || !!program;
  if (isLegacyPath && isProgramContent) {
    return <Navigate to={`/program-information/${slug}${location.search}`} replace data-id="element-1647" data-mp="3a7y2iub7nxnh" />;
  }
  if (slug === programsIndexSlug) {
    return <ProgramDomainsPage data-id="element-1648" data-mp="366ukc15oxpj3" />;
  }
  if (program) {
    return <ProgramTemplatePage key={program.slug} program={program} data-id="element-1649" data-mp="3181f0w0k54u0" />;
  }
  return <FindDegreesLandingTemplate data-id="element-1650" data-mp="2w8f44t87c0od" />;
}
function FindDegreesLandingTemplate() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  // Each live page is also declared as its own explicit route so it is listed
  // by name, and those routes carry no `:template` param — so fall back to the
  // last path segment.
  const slug = template || location.pathname.split('/').filter(Boolean).pop() || '';
  const data = slug ? getFindDegreesTemplate(slug) : undefined;
  usePageMeta(data?.metaTitle || 'Find Your Degree | FindOnlineColleges', data?.metaDescription || 'Find accredited online degree programs that fit your goals.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1651" data-mp="2rk5jnvzpd6b4">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1652" data-mp="2vl93etkvfmhm">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1653" data-mp="2vj4n1c5scv0g">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1654" data-mp="3o1s5skxb7xyd">
          Return Home
        </Link>
      </div>;
  }
  return <DegreeLandingTemplate data={data} data-id="element-1655" data-mp="17nq6g8fy632s" />;
}